// @ts-nocheck
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { 
  Settings, 
  FileText, 
  Save, 
  Play,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Edit3,
  Eye,
  Server,
  Globe,
  RefreshCw,
  RotateCcw,
  Download,
  Database,
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  X
} from 'lucide-react'
import { apiService } from '../services/api'

interface Zone {
  id: number
  zone_name: string
  zone_file: string
  template_name: string
  status: string
  is_reverse: boolean
  variables: Record<string, string>
}

interface GlobalConfig {
  config_key: string
  config_value: string
  description: string
  category: string
}

const ConfigBuilderEnhanced = () => {
  const [activeTab, setActiveTab] = useState<'zones' | 'global' | 'import'>('zones')
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [deployStatus, setDeployStatus] = useState<string | null>(null)
  const [importStatus, setImportStatus] = useState<string | null>(null)
  const [expandedCategory, setExpandedCategory] = useState<string | null>('network')
  const queryClient = useQueryClient()

  // Fetch zones
  const { data: zones, isLoading: zonesLoading, refetch: refetchZones } = useQuery('configZones',
    () => apiService.client.get('/config-builder?action=zones').then(r => r.data.data)
  )

  // Fetch global config
  const { data: globalConfig, isLoading: globalLoading } = useQuery('globalConfig',
    () => apiService.client.get('/config-builder?action=global-config').then(r => r.data.data)
  )

  // Import existing config mutation
  const importMutation = useMutation(
    () => apiService.client.get('/config-import?action=sync-all'),
    {
      onSuccess: (res) => {
        setImportStatus(`Imported ${res.data.data?.zones_imported || 0} zones and ${res.data.data?.global_settings_imported || 0} settings`)
        refetchZones()
        queryClient.invalidateQueries('globalConfig')
        setTimeout(() => setImportStatus(null), 5000)
      },
      onError: (error: any) => {
        setImportStatus('Import failed: ' + error.response?.data?.error)
        setTimeout(() => setImportStatus(null), 5000)
      }
    }
  )

  // Deploy mutation
  const deployMutation = useMutation(
    () => apiService.client.post('/config-builder?action=deploy', {}),
    {
      onSuccess: () => {
        setDeployStatus('Configuration deployed to DNS service successfully!')
        refetchZones()
        setTimeout(() => setDeployStatus(null), 5000)
      },
      onError: (error: any) => {
        setDeployStatus('Deployment failed: ' + error.response?.data?.error)
        setTimeout(() => setDeployStatus(null), 5000)
      }
    }
  )

  // Save zone mutation
  const saveZoneMutation = useMutation(
    (data: any) => apiService.client.post('/config-builder?action=update-zone', data),
    {
      onSuccess: () => {
        setDeployStatus('Zone saved!')
        refetchZones()
        setSelectedZone(null)
        setTimeout(() => setDeployStatus(null), 3000)
      }
    }
  )

  // Delete zone mutation
  const deleteZoneMutation = useMutation(
    (id: number) => apiService.client.delete(`/config-builder?action=delete-zone&id=${id}`),
    {
      onSuccess: () => {
        refetchZones()
        setDeployStatus('Zone deleted!')
        setTimeout(() => setDeployStatus(null), 3000)
      }
    }
  )

  // Save global config mutation
  const saveGlobalMutation = useMutation(
    (data: Record<string, string>) => apiService.client.post('/config-builder?action=update-global', data),
    {
      onSuccess: () => {
        setDeployStatus('Global settings saved!')
        queryClient.invalidateQueries('globalConfig')
        setTimeout(() => setDeployStatus(null), 3000)
      }
    }
  )

  // Group global config by category
  const configByCategory = globalConfig?.reduce((acc: Record<string, GlobalConfig[]>, item: GlobalConfig) => {
    const cat = item.category || 'other'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(item)
    return acc
  }, {}) || {}

  const categories: Record<string, string> = {
    'network': 'Network Settings',
    'security': 'Security Settings',
    'logging': 'Logging Settings',
    'performance': 'Performance Settings',
    'other': 'Other Settings'
  }

  const handleZoneEdit = (zone: Zone) => {
    setSelectedZone(zone)
    setFormData(zone.variables || {})
    setIsCreating(false)
  }

  const handleZoneSave = () => {
    if (selectedZone) {
      saveZoneMutation.mutate({
        id: selectedZone.id,
        variables: formData
      })
    }
  }

  const handleGlobalSave = (key: string, value: string) => {
    saveGlobalMutation.mutate({ [key]: value })
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">DNS Configuration Manager</h1>
          <p className="text-slate-500 mt-1">Manage DNS zones and global settings from database</p>
        </div>
        
        <div className="flex items-center gap-3">
          {deployStatus && (
            <div className={`px-4 py-2 rounded-lg text-sm ${deployStatus.includes('success') || deployStatus.includes('saved') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {deployStatus}
            </div>
          )}
          
          <button
            onClick={() => setActiveTab('import')}
            className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
          >
            <Download className="h-4 w-4" />
            Import Existing
          </button>
          
          <button
            onClick={() => deployMutation.mutate()}
            disabled={deployMutation.isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Play className={`h-4 w-4 ${deployMutation.isLoading ? 'animate-pulse' : ''}`} />
            {deployMutation.isLoading ? 'Deploying...' : 'Apply Changes'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('zones')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'zones' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Globe className="h-4 w-4" />
          Zones ({zones?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('global')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'global' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Settings className="h-4 w-4" />
          Global Settings
        </button>
        <button
          onClick={() => setActiveTab('import')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'import' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Database className="h-4 w-4" />
          Import
        </button>
      </div>

      {/* Zones Tab - Table View */}
      {activeTab === 'zones' && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">DNS Zones</h3>
            <button
              onClick={() => { setIsCreating(true); setSelectedZone(null); setFormData({}); }}
              className="flex items-center gap-1 px-3 py-1.5 bg-sky-600 text-white text-sm rounded-lg hover:bg-sky-700"
            >
              <Plus className="h-4 w-4" />
              Add Zone
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Zone Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Template</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Storage</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {zonesLoading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading zones...</td></tr>
                ) : zones?.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No zones found. Import existing or add new.</td></tr>
                ) : zones?.map((zone: Zone) => (
                  <tr key={zone.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{zone.zone_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${zone.is_reverse ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {zone.is_reverse ? 'Reverse' : 'Forward'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{zone.template_name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        zone.status === 'active' ? 'bg-green-100 text-green-700' :
                        zone.status === 'draft' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {zone.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">Managed by DNS service</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleZoneEdit(zone)}
                          className="p-1.5 text-sky-600 hover:bg-sky-50 rounded"
                          title="Edit zone"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteZoneMutation.mutate(zone.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Delete zone"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Zone Edit Modal */}
      {selectedZone && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Edit Zone: {selectedZone.zone_name}</h3>
              <button onClick={() => setSelectedZone(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {Object.entries(selectedZone.variables || {}).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-slate-700 mb-1 capitalize">
                    {key.replace(/_/g, ' ')}
                  </label>
                  <input
                    type="text"
                    value={formData[key] || value || ''}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                  />
                </div>
              ))}
            </div>
            
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setSelectedZone(null)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleZoneSave}
                disabled={saveZoneMutation.isLoading}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
              >
                {saveZoneMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Settings Tab - Accordion View */}
      {activeTab === 'global' && (
        <div className="space-y-4">
          {Object.entries(configByCategory).map(([category, items]) => (
            <div key={category} className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
              <button
                onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                className="w-full px-6 py-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Settings className="h-5 w-5 text-slate-500" />
                  <span className="font-semibold text-slate-900">{categories[category] || category}</span>
                  <span className="text-sm text-slate-500">({items.length} settings)</span>
                </div>
                {expandedCategory === category ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
              </button>
              
              {expandedCategory === category && (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((item: GlobalConfig) => (
                      <div key={item.config_key} className="space-y-1">
                        <label className="block text-sm font-medium text-slate-700">
                          {item.config_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            defaultValue={item.config_value}
                            onBlur={(e) => handleGlobalSave(item.config_key, e.target.value)}
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                          />
                        </div>
                        <p className="text-xs text-slate-500">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Import Tab */}
      {activeTab === 'import' && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <div className="max-w-2xl">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Import Existing Configuration</h3>
            
            {importStatus && (
              <div className={`mb-6 p-4 rounded-lg ${importStatus.includes('failed') ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                {importStatus}
              </div>
            )}
            
            <p className="text-slate-600 mb-6">
              Import existing DNS service configuration and zone files into the database. This allows you to:
            </p>
            
            <ul className="space-y-2 text-slate-600 mb-6">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Edit zones in table format
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Modify global settings via forms
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Deploy changes safely with validation
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Keep configuration history in database
              </li>
            </ul>
            
            <div className="flex gap-4">
              <button
                onClick={() => importMutation.mutate()}
                disabled={importMutation.isLoading}
                className="flex items-center gap-2 px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50"
              >
                <ArrowRightLeft className={`h-5 w-5 ${importMutation.isLoading ? 'animate-pulse' : ''}`} />
                {importMutation.isLoading ? 'Importing...' : 'Import DNS Service Configuration'}
              </button>
              
              <button
                onClick={() => setActiveTab('zones')}
                className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Skip Import
              </button>
            </div>
            
            <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <strong>Note:</strong> This will read:
                  <ul className="mt-1 ml-4 list-disc">
                    <li>Global DNS service options</li>
                    <li>Forward zone files</li>
                    <li>Reverse zone files</li>
                  </ul>
                  Existing data in database will be preserved (no duplicates created).
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ConfigBuilderEnhanced
